import React from 'react';

import type { EventRecord as Event } from '@vlabs/api/artemy/model';
import { getArtemySampleUrl } from '@vlabs/api/axios/point2-client';
import { AttentionCircleFilledIcon, DetectionIcon } from '@vlabs/icons';
import { MultiSample } from '@vlabs/monokit/shared/ui/multi-sample';
import { StreamLinkPoint3 } from '@vlabs/monokit/shared/ui/stream-link/StreamLink.point3';
import { useHasPermission, usePageAccess } from '@vlabs/permissions';
import { SimilarityTag } from '@vlabs/shared/components/similarity-tag/SimilarityTag';
import { formatDateTime } from '@vlabs/shared/utils/date';
import {
  Tag, Control, useWindowSize, FaceSample,
} from '@vlabs/uikit';
import { routes } from '@vlapps/point/src/app/routes';
import { buildLocationString } from '@vlapps/point/src/utils/location';
import cn from 'classnames';
import { useTranslation } from 'react-i18next';
import { generatePath } from 'react-router-dom';

import { BaseEventPreview } from './BaseEventPreview';
import st from './EventPreviewFaceBody.module.sass';
import { useEventInfoSettingsLocation } from '../../useEventInfoSettingsSearchParams';
import { useMatchData } from '../../useMatchData.js';
import { useSelectedWorkspaceEvent } from '../../useSelectedWorkspaceEvent';

interface EventPreviewFaceBodyProps {
  event: Event;
  onClick: (params: { event: Event }) => void;
}

export function EventPreviewFaceBody({ event, onClick }: EventPreviewFaceBodyProps): React.JSX.Element {
  const { t } = useTranslation();
  const windowSize = useWindowSize();
  const { eventInfoSettings } = useEventInfoSettingsLocation();
  const { selectedEventId } = useSelectedWorkspaceEvent();

  const matchData = useMatchData(event?.top_match);

  const location = buildLocationString(event);

  const isActive = selectedEventId === event?.event_id;
  const canReadAlarm = useHasPermission('alarm', 'read');
  const isAlarm = canReadAlarm && event?.tags?.includes('alarm');

  const canReadStream = useHasPermission('stream', 'read');
  const canViewStreamWatch = usePageAccess('streams_fs');
  const canViewStreamDetails = usePageAccess('stream_details');
  const canAccessStreamPage = canViewStreamWatch || canViewStreamDetails;

  const canReadList = useHasPermission('list', 'read');
  const canViewListDetails = usePageAccess('lists');

  const faceDetection = event?.face_detections?.find(({ sample_id }) => !!sample_id);
  const faceDetectionUrl = getArtemySampleUrl('faces', faceDetection?.sample_id);

  const bodyDetection = event?.body_detections?.find(({ sample_id }) => !!sample_id);
  const bodyDetectionUrl = getArtemySampleUrl('bodies', bodyDetection?.sample_id);

  const renderLists = () => {
    if (!matchData?.listId && !matchData?.label) {
      return <span>{t('lunapoint:карточка персоны.no lists')}</span>;
    }

    if (!matchData.listId) {
      return (
        <React.Fragment>
          <span>{t('lunapoint:карточка персоны.параметр сравнения')}</span>
          <span className={st.ListLink}>{matchData.label}</span>
        </React.Fragment>
      );
    }

    return (
      <React.Fragment>
        <span>{t('lunapoint:карточка персоны.список')}</span>
        <div className={st.ListsList}>
          {!!canViewListDetails && (
            <Control.Link
              className={st.ListLink}
              kind="secondary"
              to={generatePath(routes.lists.read, { listId: matchData.listId })}
            >
              {matchData.listLabel ?? matchData.listId}
            </Control.Link>
          )}
          {!canViewListDetails && (
            <span className={st.ListLink}>
              {matchData.listLabel ?? matchData.listId}
            </span>
          )}
        </div>
      </React.Fragment>
    );
  };

  return (
    <BaseEventPreview
      data-testid={event?.event_id}
      isActive={isActive}
      isAlarm={isAlarm}
      onClick={() => onClick({ event })}
    >
      <BaseEventPreview.Main>
        <BaseEventPreview.Media>
          <div className={st.Samples}>
            <MultiSample
              className={st.MultiSample}
              data-testid="eventInformationCell.sample"
              mainSample={eventInfoSettings?.mainSample}
              primary={{
                imageUrl: faceDetectionUrl,
                type: 'face',
              }}
              sampleType={eventInfoSettings?.sampleType}
              secondary={{
                imageUrl: bodyDetectionUrl,
                type: 'body',
              }}
            />

            {!!matchData && (
              <FaceSample
                className={st.MultiSample}
                data-testid="eventInformationCell.matchSample"
                src={matchData.uiAvatarUrl}
              />
            )}
          </div>
        </BaseEventPreview.Media>

        <BaseEventPreview.Info>
          <div className={st.SamplesData}>
            <div className={st.Tags}>
              <Tag
                className={isAlarm ? st.Tag_alarm : ''}
                data-testid="eventInformationCell.tagType"
                icon={
                  isAlarm ? (
                    <AttentionCircleFilledIcon className={st.PulsingIcon} />
                  ) : (
                    ''
                  )
                }
                kind="neutral"
                size={windowSize.width < 1920 ? 'medium' : 'large'}
                value={t(
                  `lunapoint:карточка события.${event?.top_match ? 'совпадение' : 'обнаружение'}`,
                )}
              />

              {!!event?.top_match && (
                <SimilarityTag
                  data-testid="eventInformationCell.similarityTagType"
                  icon={<DetectionIcon />}
                  size={windowSize.width < 1920 ? 'medium' : 'large'}
                  title={t('lunapoint:карточка персоны.карточка')}
                  value={event?.top_match?.similarity}
                />
              )}
            </div>

            {!!eventInfoSettings.isVisibleDate && (
              <div
                className={st.InfoText}
                data-testid="eventInformationCell.createTime"
              >
                {formatDateTime(event?.create_time)}
              </div>
            )}

            {!!eventInfoSettings.isVisibleSource && !!canReadStream && (
              <div
                className={cn(st.InfoText, st.InfoText_empty)}
                data-testid="eventInformationCell.sources"
              >
                {!!event?.stream_id && !!canAccessStreamPage && (
                  <StreamLinkPoint3
                    className={st.StreamLink}
                    hasTruncatedLink
                    hideId
                    key={!!matchData}
                    kind="secondary"
                    placeholders={{
                      removed: t(
                        'lunapoint:карточка события.videostream.removed',
                      ),
                      unavailable: t(
                        'lunapoint:карточка события.videostream.unavailable',
                      ),
                    }}
                    size="medium"
                    streamId={event.stream_id}
                  />
                )}
                {!!event?.stream_id && !canAccessStreamPage && (
                  <span className={st.StreamLink}>
                    {event.source}
                  </span>
                )}
                {!event?.stream_id && !!event?.source && (
                  <div
                    className={cn(st.InfoText, {
                      [st.InfoText_empty]:
                        location === t('lunapoint:источник отсутствует'),
                    })}
                  >
                    {event.source}
                  </div>
                )}
              </div>
            )}
          </div>
        </BaseEventPreview.Info>
      </BaseEventPreview.Main>

      {!!(eventInfoSettings.isVisibleAddress || (eventInfoSettings.isVisibleLists && canReadList)) && (
        <BaseEventPreview.Footer>
          {!!eventInfoSettings.isVisibleAddress && (
            <div
              className={cn(st.InfoText, {
                [st.InfoText_empty]:
                  location === t('lunapoint:адрес отсутствует'),
              })}
              data-testid="eventInformationCell.location"
            >
              {location}
            </div>
          )}

          {!!eventInfoSettings.isVisibleLists && !!canReadList && (
            <div
              className={st.Lists}
              data-testid="eventInformationCell.lists"
            >
              {renderLists()}
            </div>
          )}
        </BaseEventPreview.Footer>
      )}
    </BaseEventPreview>
  );
}
